import { useLocation } from 'react-router-dom';
import { Formik } from 'formik';
import { noop } from 'lodash';

import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import { Addresses as AddressesCmp } from '../../components/Addresses';
import { ThirdPartyAddressesFormStep } from '../../forms/ThirdParty/steps/Addresses';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import type { AddressPaths } from '../../forms/ThirdParty/steps/Addresses/types/paths.type';

export const PersonPage_Addresses_Person_Fragment = graphql(`
  fragment PersonPage_Addresses_Person_Fragment on Person {
    _id
    isInternal
    kind
    statusId
    addresses {
      _id
      typeIds
      postalInfo {
        _id
        postOfficeBox
        postalCode
        region
        route
        streetNumber
        additional
        building
        businessPostCode {
          postalCode
          distributorOfficeNumber
        }
        locality
        inseeCode
        department
        countryAlpha2
      }
      locationInfo {
        _id
        coordinates
        projectionId
        altitude
      }
      cadastralInfo {
        _id
        departmentCode
        districtCode
        municipalityCode
        number
        oldMunicipalityCode
        section
        sheet
      }
      ...AddressesCmp_Address_Fragment
    }
    pennylaneCustomerBridges {
      _id
    }
    ...ThirdPartyForm_Person_Fragment
    ...ThirdPartyForm_AddressesStep_ThirdParty_Fragment
  }
`);

interface Props {
  readonly paths?: AddressPaths;
  readonly person: FragmentType<typeof PersonPage_Addresses_Person_Fragment>;
}

export const Addresses = ({ paths, person: pPerson }: Readonly<Props>) => {
  const { addressableConfigsCodeMap } = useReferencesContext();

  const person = getFragmentData(PersonPage_Addresses_Person_Fragment, pPerson);

  const { pathname: currentPath } = useLocation();

  const isInEditionMode = paths && currentPath !== paths.consult;

  const addressableConfig = person.isInternal
    ? addressableConfigsCodeMap.InternalPerson
    : person.pennylaneCustomerBridges?.length
      ? addressableConfigsCodeMap.ExternalThirdPartySyncWithPennylane
      : addressableConfigsCodeMap.ExternalThirdParty;

  return (
    <>
      {!isInEditionMode ? (
        <AddressesCmp actionsEnabled={false} addresses={person.addresses} />
      ) : (
        <Formik enableReinitialize initialValues={person} onSubmit={noop}>
          <ThirdPartyAddressesFormStep
            config={addressableConfig}
            id="personAddresses"
            paths={paths}
            useDirectMutation
          />
        </Formik>
      )}
      {!!paths && (
        <SpeedDial
          actions={
            isInEditionMode
              ? [
                  {
                    id: 'leaveEditionMode',
                    icon: <EditOffIcon />,
                    name: 'Quitter Mode Édition',
                    to: paths.consult,
                  },
                ]
              : [
                  {
                    id: 'editPersonAddresses',
                    icon: <EditIcon />,
                    name: 'Modifier Adresses',
                    to: paths.edit,
                  },
                ]
          }
          title="Actions possibles sur les Adresses de la Personne"
        />
      )}
    </>
  );
};
